import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';

// Premium Role Form - Sophisticated Access Definition
function RoleForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [roleData, setRoleData] = useState<any>(null);

    useEffect(() => {
        if (isEdit) {
            loadRole();
        }
    }, [id]);

    const loadRole = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/settings/roles/${id}`);
            if (response.success) {
                const role = response.data.role || response.data;
                setRoleData(role);
                setFormData({
                    name: role.name || '',
                    description: role.description || ''
                });
            } else {
                setError(response.message || 'Verification failed for security tier retrieval');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError(null);

            const response = isEdit
                ? await api.put(`/settings/roles/${id}`, { role: formData })
                : await api.post('/settings/roles', { role: formData });

            if (response.success) {
                navigate('/settings/roles');
            } else {
                setError(response.message || 'System error during security tier synthesis');
            }
        } catch (err: any) {
            setError(err.message || 'Critical communication failure');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <div className="text-slate-500 font-black tracking-widest uppercase text-[10px]">Accessing Security Protocols...</div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex items-center justify-between mb-10">
                <div>
                    <h1 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
                        {isEdit ? 'Refine' : 'Design'} <span className="text-indigo-600">Access Tier</span>
                    </h1>
                    <p className="text-slate-500 mt-1 font-medium italic">Define functional boundaries and security scopes</p>
                </div>
                <Link to="/settings/roles" className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </Link>
            </div>

            <div className="bg-white rounded-lg border border-slate-100 shadow-2xl shadow-slate-200/50 p-8 sm:p-12">
                <form onSubmit={handleSubmit} className="space-y-10">
                    {error && (
                        <div className="bg-rose-50 border-l-4 border-rose-500 p-5 rounded-2xl shadow-sm animate-bounce-short">
                            <div className="flex gap-3 items-center">
                                <svg className="w-5 h-5 text-rose-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm font-black text-rose-800 tracking-wide uppercase">{error}</p>
                            </div>
                        </div>
                    )}

                    {roleData?.system_role && (
                        <div className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-2xl shadow-sm mb-4">
                            <div className="flex gap-3">
                                <svg className="w-5 h-5 text-amber-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <div>
                                    <p className="text-sm font-black text-amber-800 tracking-wide uppercase">Core System Role</p>
                                    <p className="text-xs font-bold text-amber-700/70 mt-1">Foundational roles are restricted and essential for organizational integrity.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="space-y-8">
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] pl-1">Role Designation <span className="text-rose-500">*</span></label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                disabled={roleData?.system_role}
                                className={`w-full px-6 py-5 rounded-3xl outline-none transition-all font-black shadow-sm text-lg ${roleData?.system_role
                                    ? 'bg-slate-100 text-slate-400 border-none cursor-not-allowed'
                                    : 'bg-slate-50 border border-slate-100 text-slate-900 focus:bg-white focus:border-indigo-500'
                                    }`}
                                placeholder="e.g. Sales Executive, Logistics Manager"
                            />
                            <p className="text-[10px] text-slate-400 font-bold italic pl-1">A clear, professional title for organizational identification</p>
                        </div>

                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] pl-1 text-emerald-600">Scope Description</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={4}
                                className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:bg-white focus:border-emerald-500 transition-all font-bold text-slate-700 shadow-sm"
                                placeholder="Succinctly define the responsibilities and boundaries of this position..."
                            />
                            <p className="text-[10px] text-slate-400 font-bold italic pl-1 text-emerald-600 uppercase tracking-tighter">Helpful for future personnel audits</p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-4 pt-10 border-t border-slate-50">
                        <Link
                            to="/settings/roles"
                            className="px-8 py-5 bg-white border border-slate-200 text-slate-500 rounded-3xl font-black uppercase tracking-[0.2em] text-[10px] transition-all hover:bg-slate-50 text-center"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            disabled={saving}
                            className={`px-12 py-5 bg-slate-900 text-white rounded-3xl font-black uppercase tracking-[0.2em] text-[10px] transition-all shadow-2xl shadow-indigo-100 hover:bg-black hover:scale-105 active:scale-95 ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {saving ? 'Processing...' : (isEdit ? 'Refine Scope' : 'Seal Tier')}
                        </button>
                    </div>
                </form>
            </div>

            {isEdit && (
                <div className="mt-8 animate-in slide-in-from-top-4 duration-1000">
                    <Link
                        to={`/settings/roles/${id}/permissions`}
                        className="flex items-center justify-between p-8 bg-indigo-600 rounded-lg text-white shadow-2xl shadow-indigo-100 hover:scale-[1.02] transition-all group"
                    >
                        <div className="flex items-center gap-6">
                            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md">
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-black text-xl tracking-tight">Access Matrix</h3>
                                <p className="text-white/60 text-xs font-bold uppercase tracking-widest mt-0.5 group-hover:text-white transition-colors">Configure granular functional permissions</p>
                            </div>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white group-hover:text-indigo-600 transition-all">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </Link>
                </div>
            )}
        </div>
    );
}

export default RoleForm;
